import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import LeftPanel from '../components/LeftPanel';
import PrimaryButton from '../components/PrimaryButton';
import InfoModal from '../components/InfoModal';
import axios from 'axios';

function GrantLandingPage() {
  const navigate = useNavigate();
  const [cigCompleted, setCigCompleted] = useState(false);
  const [ilgCompleted, setIlgCompleted] = useState(false);
  const [cigInProgress, setCigInProgress] = useState(false);
  const [ilgInProgress, setIlgInProgress] = useState(false);
  const [pastIlgWinner, setPastIlgWinner] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const cig = await axios.get('/api/public/cig-applications', { headers: token ? { Authorization: `Bearer ${token}` } : {} });
        if (cig?.data?.exists && cig?.data?.completed) {
          setCigCompleted(true);
          setCigInProgress(false);
        } else if (cig?.data?.exists && !cig?.data?.completed) {
          setCigCompleted(false);
          setCigInProgress(true);
        } else {
          setCigCompleted(false);
          setCigInProgress(false);
        }
      } catch (e) {
        setCigCompleted(false);
        setCigInProgress(false);
      }
      try {
        const token = localStorage.getItem('token');
        const ilg = await axios.get('/api/public/ilg-applications', { headers: token ? { Authorization: `Bearer ${token}` } : {} });
        if (ilg?.data?.exists && ilg?.data?.completed) {
          setIlgCompleted(true);
          setIlgInProgress(false);
        } else if (ilg?.data?.exists && !ilg?.data?.completed) {
          setIlgCompleted(false);
          setIlgInProgress(true);
        } else {
          setIlgCompleted(false);
          setIlgInProgress(false);
        }
      } catch (e) {
        setIlgCompleted(false);
        setIlgInProgress(false);
      }
      // fetch current user / school info to determine past winner flag
      try {
        const token = localStorage.getItem('token');
        const me = await axios.get('/api/public/me', { headers: token ? { Authorization: `Bearer ${token}` } : {} });
        if (me?.data?.pastIlgWinner) {
          setPastIlgWinner(true);
        } else {
          setPastIlgWinner(false);
        }
      } catch (e) {
        setPastIlgWinner(false);
      }
    })();
  }, []);
  const [infoOpen, setInfoOpen] = useState(false);
  const [infoType, setInfoType] = useState('faq');
  const [downloading, setDownloading] = useState('');

  const downloadCompleted = async (key) => {
    try {
      setDownloading(key);
      const token = localStorage.getItem('token');
      const url = `/api/public/${key}-applications/download`;
      const res = await axios.get(url, { responseType: 'blob', headers: token ? { Authorization: `Bearer ${token}` } : {} });
      const contentType = res.headers['content-type'] || 'application/pdf';
      const blob = new Blob([res.data], { type: contentType });
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      // Prefer a meaningful filename for users; map known keys to official names
      const FILE_NAME_MAP = {
        cig: '2026-Retool-Your-School-Campus-Improvement-Grant-Application.pdf',
        ilg: '2026-Retool-Your-School-Innovation-Lounge-Grant-Application.pdf'
      };
      const mapped = FILE_NAME_MAP[key];
      if (mapped) {
        a.download = mapped;
      } else {
        a.download = `${key}-application.${contentType.includes('pdf') ? 'pdf' : 'bin'}`;
      }
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (e) {
      console.error('Download failed', e);
      alert('Failed to download application.');
    } finally {
      setDownloading('');
    }
  };

  // Grant card data
  const grants = [
    {
      key: 'cig',
      title: 'Campus Improvement Grant',
      description: `The Campus Improvement Grant provides funding to support impactful upgrades to campus facilities and shared spaces. This grant is designed to help institutions improve high-traffic, high-impact areas such as classrooms, common areas, outdoor spaces, and student service facilities. Funded projects typically focus on modernization, functionality, accessibility, and overall campus experience—creating environments that better support learning, engagement, and community use.`,
      image: 'cig.png',
      buttonText: 'APPLY NOW',
      completed: cigCompleted,
      inProgress: cigInProgress,
      onClick: () => navigate('/cigform'),
    },
    {
      key: 'ilg',
      title: 'Innovation Lounge Grant',
      description: `The Innovation Lounge Grant supports the transformation of existing campus spaces into modern, collaborative environments designed for creativity, teamwork, and experiential learning. These projects often include flexible furnishings, updated technology, and reimagined layouts that encourage students to gather, ideate, and work together. The goal is to create dynamic, multi-purpose spaces that foster innovation, collaboration, and student engagement across academic and co-curricular activities.`,
      image: 'ilg.png',
      buttonText: 'APPLY NOW',
      completed: ilgCompleted,
      inProgress: ilgInProgress,
      pastIlgWinner: pastIlgWinner,
      onClick: () => navigate('/ilgform'),
    },
  ];

  return (
    <Box component="main">
      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', mt: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, maxWidth: 900, width: '100%' }}>
          {grants.map(grant => (
            <Paper key={grant.key} elevation={2} sx={{ position: 'relative', flex: 1, p: { xs: 3, md: 4 }, display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 340, maxWidth: 420, minHeight: 420 }}>
              {grant.inProgress && (
                <Box sx={{ position: 'absolute', left: 0, top: 0, width: '100%', bgcolor: '#ff6600', color: '#fff', textAlign: 'center', py: 0.1, borderTopLeftRadius: 4, borderTopRightRadius: 4 }}>
                  <Typography sx={{ fontWeight: 700 }}>In Progress</Typography>
                </Box>
              )}
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, textAlign: 'center' }}>{grant.title}</Typography>
              <Typography variant="body1" sx={{ mb: 2, textAlign: 'center' }}>{grant.description}</Typography>
              <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', mb: 2 }}>
                <Box component="img" src={grant.image} alt={grant.title} sx={{ maxWidth: 340, width: '100%', objectFit: 'cover', boxShadow: 1 }} />
              </Box>
              {grant.key === 'ilg' && grant.pastIlgWinner ? (
                <PrimaryButton
                  disabled
                  sx={{ bgcolor: '#e0e0e0', color: 'text.disabled', fontWeight: 700, fontSize: 16, px: 3, py: 1.5, mt: 1 }}
                >
                  Past Winner - Not Eligible
                </PrimaryButton>
              ) : (
                <PrimaryButton
                  onClick={grant.onClick}
                  disabled={grant.completed}
                  sx={{ bgcolor: grant.completed ? '#e0e0e0' : '#ff6600', color: grant.completed ? 'text.disabled' : '#fff', fontWeight: 700, fontSize: 16, px: 3, py: 1.5, mt: 1 }}
                >
                  {grant.completed ? (grant.key === 'cig' ? 'Campus Improvement — Submitted' : 'Innovation Lounge — Submitted') : (grant.inProgress ? 'CONTINUE' : grant.buttonText)}
                </PrimaryButton>
              )}
              {grant.completed && (
                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', mt: 1 }}>
                  <Button
                    variant="text"
                    onClick={() => downloadCompleted(grant.key)}
                    disabled={downloading === grant.key}
                    sx={{ textTransform: 'none' }}
                  >
                    {downloading === grant.key ? 'Preparing Download...' : 'Download Completed Application'}
                  </Button>
                </Box>
              )}
            </Paper>
          ))}
        </Box>
      </Box>
      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', mt: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', textAlign: 'center' }}>
          Grant Recipients Publicly Announced: August 2026
        </Typography>
      </Box>
    </Box>
  );
}
export default GrantLandingPage;
